import React, { useState } from 'react';
import { formatExchangeRate } from '../../utils/formatters';

// ─── Tipler ───────────────────────────────────────────────────────────────────

/** Backend ExchangeRateInfoDto */
export interface ExchangeRateInfo {
  missingCurrencies: string[];
  manualRates: Record<string, number>;
}

/** Netsis'te kur yoksa kullanıcının o an için girdiği kurlar (kaydedilmez) */
export interface ManualRates {
  usdRate?: number;
  eurRate?: number;
}

// Backend sadece USD ve EUR için elle kur kabul ediyor
const SUPPORTED = {
  USD: { label: 'dolar', name: 'Dolar', key: 'usdRate' },
  EUR: { label: 'euro', name: 'Euro', key: 'eurRate' },
} as const;

type SupportedCurrency = keyof typeof SUPPORTED;

const isSupported = (code: string): code is SupportedCurrency => code in SUPPORTED;

const promptableCurrencies = (info?: ExchangeRateInfo | null): SupportedCurrency[] =>
  (info?.missingCurrencies ?? []).map(c => c.toUpperCase()).filter(isSupported);

/** Kullanıcıdan kur istenmesi gerekiyor mu */
export const needsRatePrompt = (info?: ExchangeRateInfo | null): boolean =>
  promptableCurrencies(info).length > 0;

// "45.54" veya "45,54"; en fazla 2 ondalık
const RATE_PATTERN = /^\d+([.,]\d{1,2})?$/;

const parseRate = (text: string): number | null => {
  const trimmed = text.trim();
  if (!RATE_PATTERN.test(trimmed)) return null;
  const value = parseFloat(trimmed.replace(',', '.'));
  return value > 0 ? value : null;
};

// ─── Kur soran pencere ────────────────────────────────────────────────────────

interface ExchangeRatePromptProps {
  info: ExchangeRateInfo;
  onSubmit: (rates: ManualRates) => void;
  onSkip: () => void;
}

export const ExchangeRatePrompt: React.FC<ExchangeRatePromptProps> = ({ info, onSubmit, onSkip }) => {
  const currencies = promptableCurrencies(info);
  const [values, setValues] = useState<Record<string, string>>({});

  const parsed = currencies.map(c => parseRate(values[c] ?? ''));
  const allValid = parsed.every(v => v != null);
  const currencyText = currencies.map(c => SUPPORTED[c].label).join(' ve ');

  const handleSubmit = () => {
    if (!allValid) return;
    const rates: ManualRates = {};
    currencies.forEach((c, i) => {
      rates[SUPPORTED[c].key] = parsed[i]!;
    });
    onSubmit(rates);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 transition-colors duration-300">
        <form
          className="p-6"
          onSubmit={e => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="flex items-center mb-4">
            <div className="bg-yellow-100 dark:bg-yellow-900/30 rounded-lg p-3 mr-4">
              <i className="fas fa-exclamation-circle text-yellow-600 dark:text-yellow-400 text-xl"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Bugünün kuru Netsis'te yok</h3>
          </div>

          <div className="mb-4 space-y-2 text-gray-700 dark:text-gray-300 leading-relaxed">
            <p>
              Netsis'te bugünün {currencyText} kuru henüz yok. Dolar karşılıklarını görmek için lütfen bugünün{' '}
              {currencyText} kurunu giriniz.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Girdiğiniz kur kaydedilmez, sadece bu açılışta kullanılır. Kur girmeden geçerseniz dolar karşılığı
              sütununu göremezsiniz.
            </p>
          </div>

          <div className="mb-6 space-y-3">
            {currencies.map((c, i) => {
              const text = values[c] ?? '';
              const invalid = text.trim() !== '' && parsed[i] == null;
              return (
                <label key={c} className="block">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Bugünün {SUPPORTED[c].label} kuru ({c})
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoFocus={i === 0}
                    placeholder="45.54"
                    value={text}
                    onChange={e => setValues(prev => ({ ...prev, [c]: e.target.value }))}
                    className={`mt-1 w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      invalid ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {invalid && (
                    <span className="text-xs text-red-600 dark:text-red-400">
                      Kuru 45.54 biçiminde giriniz (en fazla 2 ondalık).
                    </span>
                  )}
                </label>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button
              type="button"
              onClick={onSkip}
              className="px-4 py-3 sm:py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-base"
            >
              Kur girmeden geç
            </button>
            <button
              type="submit"
              disabled={!allValid}
              className={`px-4 py-3 sm:py-2 rounded-lg transition-colors text-base text-white ${
                allValid ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-400 dark:bg-blue-800 cursor-not-allowed'
              }`}
            >
              Kurla devam et
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Elle girilen kur notu ────────────────────────────────────────────────────

export const ManualRateNote: React.FC<{ info?: ExchangeRateInfo | null | undefined; className?: string }> = ({
  info,
  className = '',
}) => {
  const entries = Object.entries(info?.manualRates ?? {});
  if (entries.length === 0) return null;
  const text = entries
    .map(([c, rate]) => {
      const code = c.toUpperCase();
      return `${isSupported(code) ? SUPPORTED[code].name : c} ${formatExchangeRate(rate)}`;
    })
    .join(' · ');
  return (
    <div
      className={`text-xs px-3 py-1.5 rounded bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800 ${className}`}
    >
      <i className="fas fa-info-circle mr-1"></i>
      Dolar karşılıkları elle girilen kurla hesaplandı: {text}
    </div>
  );
};
